<template>
  <div class="auth-page">
    <div class="container py-5">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-md-6 col-lg-5">
          <div class="auth-card p-4 shadow rounded-4">
          <!-- Logo/Título -->
          <div class="text-center mb-4">
            <img src="@/assets/images/LOGO.svg" alt="Kame Seeds logo" class="img-fluid col-3">
            <h4 class="text-light mt-3">{{ pageTitle }}</h4>
          </div>

          <!-- Pantalla post-registro: link de activación -->
          <div v-if="showActivationPending" class="activation-pending">
            <div class="activation-icon mb-4">
              <i class="fas fa-envelope-open-text"></i>
            </div>
            <p class="text-center text-white mb-3">
              Te vamos a enviar un email con un <strong>link de activación</strong> a:
            </p>
            <p class="text-center mb-4">
              <strong class="text-primary">{{ registeredEmail || formData.email }}</strong>
            </p>
            <p class="text-center text-muted small mb-4">
              Abrí el link del email para activar tu cuenta. Revisá también la carpeta de spam.
              El envío puede tardar unos minutos.
            </p>

            <div v-if="registerPending" class="text-center text-muted small mb-3">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Confirmando registro…
            </div>

            <div class="text-center mb-3">
              <button
                @click="resendActivationLink"
                class="btn btn-outline-light w-100 py-2"
                :disabled="isResending"
              >
                <span v-if="isResending" class="spinner-border spinner-border-sm me-2"></span>
                {{ isResending ? 'Enviando…' : 'Reenviar email de activación' }}
              </button>
            </div>

            <div class="text-center">
              <router-link to="/login" class="btn btn-link text-primary">
                Ir a Iniciar Sesión
              </router-link>
            </div>
          </div>

          <!-- Formulario de Registro / Solicitud de acceso -->
          <template v-else>
            <!-- Selector de modo -->
            <div v-if="!showRequestSuccess" class="mode-toggle mb-4">
              <button
                type="button"
                class="mode-toggle__btn"
                :class="{ active: mode === 'code' }"
                @click="setMode('code')"
              >
                <i class="fas fa-key me-2"></i>Tengo código
              </button>
              <button
                v-if="accessRequestsEnabled"
                type="button"
                class="mode-toggle__btn"
                :class="{ active: mode === 'request' }"
                @click="setMode('request')"
              >
                <i class="fas fa-paper-plane me-2"></i>Solicitar acceso
              </button>
            </div>

            <!-- Solicitud de acceso: pantalla de éxito -->
            <div v-if="showRequestSuccess" class="activation-pending">
              <div class="activation-icon mb-4">
                <i class="fas fa-check-double"></i>
              </div>
              <p class="text-center text-white mb-3">
                ¡Solicitud enviada! Te avisaremos por email a
              </p>
              <p class="text-center mb-4">
                <strong class="text-primary">{{ requestData.email }}</strong>
              </p>
              <p class="text-center text-muted small mb-4">
                Cuando un administrador apruebe tu cuenta, vas a recibir un email
                y ya vas a poder ingresar con tu email y la contraseña que elegiste.
                Revisá también la carpeta de spam.
              </p>
              <div class="text-center">
                <router-link to="/login" class="btn btn-link text-primary">
                  Ir a Iniciar Sesión
                </router-link>
              </div>
            </div>

            <!-- Formulario con código de invitación -->
            <form v-else-if="mode === 'code'" @submit.prevent="handleSubmit" class="needs-validation" novalidate>
            <!-- Nombre y Apellido -->
            <div class="row mb-3">
              <div class="col-12 mb-3">
                <div class="form-floating">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': v$.first_name.$error }"
                    id="first_name"
                    v-model="formData.first_name"
                    placeholder="Nombre"
                    required
                  >
                  <label for="first_name">
                    <i class="fas fa-user text-muted me-2"></i>Nombre
                  </label>
                  <div class="invalid-feedback" v-if="v$.first_name.$error">
                    {{ v$.first_name.$errors[0].$message }}
                  </div>
                </div>
              </div>
              <div class="col-12">
                <div class="form-floating">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': v$.last_name.$error }"
                    id="last_name"
                    v-model="formData.last_name"
                    placeholder="Apellido"
                    required
                  >
                  <label for="last_name">
                    <i class="fas fa-user text-muted me-2"></i>Apellido
                  </label>
                  <div class="invalid-feedback" v-if="v$.last_name.$error">
                    {{ v$.last_name.$errors[0].$message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Campo Usuario -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.username.$error }"
                id="username"
                v-model="formData.username"
                placeholder="Usuario"
                required
              >
              <label for="username">
                <i class="fas fa-user text-muted me-2"></i>Usuario
              </label>
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
            </div>

            <!-- Teléfono y DNI -->
            <div class="row mb-3">
              <div class="col-12 col-md-6 mb-3 mb-md-0">
                <div class="form-floating">
                  <input
                    type="tel"
                    class="form-control"
                    :class="{ 'is-invalid': v$.phone_number.$error }"
                    id="phone_number"
                    v-model="formData.phone_number"
                    placeholder="Teléfono"
                    inputmode="numeric"
                    autocomplete="tel"
                    required
                  >
                  <label for="phone_number">
                    <i class="fas fa-phone text-muted me-2"></i>Teléfono
                  </label>
                  <div class="invalid-feedback" v-if="v$.phone_number.$error">
                    {{ v$.phone_number.$errors[0].$message }}
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="form-floating">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': v$.dni.$error }"
                    id="dni"
                    v-model="formData.dni"
                    placeholder="DNI"
                    inputmode="numeric"
                    maxlength="20"
                    required
                  >
                  <label for="dni">
                    <i class="fas fa-id-card text-muted me-2"></i>DNI
                  </label>
                  <div class="invalid-feedback" v-if="v$.dni.$error">
                    {{ v$.dni.$errors[0].$message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Campo Instagram (opcional) -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                id="instagram"
                v-model="formData.instagram"
                placeholder="Instagram (opcional)"
              >
              <label for="instagram">
                <i class="fab fa-instagram text-muted me-2"></i>Instagram (opcional)
              </label>
              
              <!-- Tooltip de información -->
              <div class="instagram-tooltip">
                <i class="fas fa-info-circle text-primary"></i>
                <div class="tooltip-content">
                  Es importante que proporciones tu Instagram ya que lo utilizaremos como medio de comunicación alternativo si no podemos contactarte por email.
                </div>
              </div>
            </div>

            <!-- Campo Email -->
            <div class="form-floating mb-3">
              <input
                type="email"
                class="form-control"
                :class="{ 'is-invalid': v$.email.$error }"
                id="email"
                v-model="formData.email"
                placeholder="Email"
                required
              >
              <label for="email">
                <i class="fas fa-envelope text-muted me-2"></i>Email
              </label>
              <div class="invalid-feedback" v-if="v$.email.$error">
                {{ v$.email.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password.$error }"
                id="password"
                v-model="formData.password"
                placeholder="Contraseña"
                required
              >
              <label for="password">
                <i class="fas fa-lock text-muted me-2"></i>Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Confirmar Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password2.$error }"
                id="confirmPassword"
                v-model="formData.password2"
                placeholder="Confirmar Contraseña"
                required
              >
              <label for="confirmPassword">
                <i class="fas fa-lock text-muted me-2"></i>Confirmar Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password2.$error">
                {{ v$.password2.$errors[0].$message }}
              </div>
            </div>

            <!-- Código de invitación -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': inviteError || v$.invitation_code?.$error }"
                id="invitation_code"
                v-model="formData.invitation_code"
                placeholder="Código de invitación"
                maxlength="20"
                required
                @input="inviteError = ''"
                @blur="validateInviteCode"
              >
              <label for="invitation_code">
                <i class="fas fa-key text-muted me-2"></i>Código de invitación
              </label>
              <div class="form-text text-muted">
                Ingresá el código que te proporcionamos para registrarte. Es obligatorio.
              </div>
              <div v-if="inviteRoleLabel" class="form-text text-success">
                <i class="fas fa-check me-1"></i>Código válido — rol: {{ inviteRoleLabel }}
              </div>
              <div class="invalid-feedback" v-if="inviteError">{{ inviteError }}</div>
              <div class="invalid-feedback" v-else-if="v$.invitation_code?.$error">
                {{ v$.invitation_code.$errors[0].$message }}
              </div>
            </div>

            <!-- Datos del negocio (según código de invitación) -->
            <template v-if="requiresBusinessInfo">
              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.business_name.$error }"
                  id="business_name"
                  v-model="formData.business_name"
                  placeholder="Nombre del negocio"
                  required
                >
                <label for="business_name">
                  <i class="fas fa-store text-muted me-2"></i>Nombre del negocio
                </label>
                <div class="invalid-feedback" v-if="v$.business_name.$error">
                  {{ v$.business_name.$errors[0].$message }}
                </div>
              </div>

              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.business_address.$error }"
                  id="business_address"
                  v-model="formData.business_address"
                  placeholder="Dirección del negocio"
                  required
                >
                <label for="business_address">
                  <i class="fas fa-map-marker-alt text-muted me-2"></i>Dirección del negocio
                </label>
                <div class="invalid-feedback" v-if="v$.business_address.$error">
                  {{ v$.business_address.$errors[0].$message }}
                </div>
              </div>

              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.business_type.$error }"
                  id="business_type"
                  v-model="formData.business_type"
                  placeholder="Tipo de negocio"
                  required
                >
                <label for="business_type">
                  <i class="fas fa-briefcase text-muted me-2"></i>Tipo de negocio
                </label>
                <div class="invalid-feedback" v-if="v$.business_type.$error">
                  {{ v$.business_type.$errors[0].$message }}
                </div>
              </div>
            </template>

            <!-- Mensaje de error -->
            <div v-if="errorMessage" class="alert alert-danger mb-3">
              {{ errorMessage }}
            </div>

            <!-- Mensaje de cuenta existente no verificada -->
            <div v-if="showAccountExistsMessage" class="alert alert-success mb-3">
              <i class="fas fa-info-circle me-2"></i>
              {{ accountExistsMessage }}
              <br>
              <router-link to="/login" class="text-success fw-bold text-decoration-none">
                <i class="fas fa-sign-in-alt me-1"></i>
                Ir a Iniciar Sesión
              </router-link>
            </div>

            <!-- Botón Submit -->
            <button
              type="submit"
              class="btn btn-primary w-100 mb-3 py-3"
              :disabled="isLoading || inviteValidating"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ isLoading ? 'Creando cuenta...' : 'Crear Cuenta' }}
            </button>

            <!-- Link a Login -->
            <div class="text-center">
              <p class="text-muted">
                ¿Ya tienes una cuenta? 
                <router-link to="/login" class="text-primary text-decoration-none">
                  Iniciar Sesión
                </router-link>
              </p>
            </div>
          </form>

            <!-- Formulario de solicitud de acceso -->
            <form v-else @submit.prevent class="needs-validation" novalidate>
              <!-- Paso 1: completar datos -->
              <template v-if="requestStep === 'form'">
              <!-- Nombre y Apellido -->
              <div class="row mb-3">
                <div class="col-12 mb-3">
                  <div class="form-floating">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': vRequest$.first_name.$error }"
                      id="req_first_name"
                      v-model="requestData.first_name"
                      placeholder="Nombre"
                      required
                    >
                    <label for="req_first_name">
                      <i class="fas fa-user text-muted me-2"></i>Nombre
                    </label>
                    <div class="invalid-feedback" v-if="vRequest$.first_name.$error">
                      {{ vRequest$.first_name.$errors[0].$message }}
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-floating">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': vRequest$.last_name.$error }"
                      id="req_last_name"
                      v-model="requestData.last_name"
                      placeholder="Apellido"
                      required
                    >
                    <label for="req_last_name">
                      <i class="fas fa-user text-muted me-2"></i>Apellido
                    </label>
                    <div class="invalid-feedback" v-if="vRequest$.last_name.$error">
                      {{ vRequest$.last_name.$errors[0].$message }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email -->
              <div class="form-floating mb-3">
                <input
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': vRequest$.email.$error }"
                  id="req_email"
                  v-model="requestData.email"
                  placeholder="Email"
                  required
                >
                <label for="req_email">
                  <i class="fas fa-envelope text-muted me-2"></i>Email
                </label>
                <div class="invalid-feedback" v-if="vRequest$.email.$error">
                  {{ vRequest$.email.$errors[0].$message }}
                </div>
              </div>

              <!-- Contraseña -->
              <div class="form-floating mb-3">
                <input
                  :type="showRequestPassword ? 'text' : 'password'"
                  class="form-control"
                  :class="{ 'is-invalid': vRequest$.password.$error }"
                  id="req_password"
                  v-model="requestData.password"
                  placeholder="Contraseña"
                  required
                >
                <label for="req_password">
                  <i class="fas fa-lock text-muted me-2"></i>Contraseña
                </label>
                <span class="password-toggle" @click="showRequestPassword = !showRequestPassword">
                  <i :class="showRequestPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </span>
                <div class="invalid-feedback" v-if="vRequest$.password.$error">
                  {{ vRequest$.password.$errors[0].$message }}
                </div>
                <div class="form-text text-muted">
                  Con esta contraseña vas a ingresar cuando aprobemos tu cuenta.
                </div>
              </div>

              <!-- Confirmar contraseña -->
              <div class="form-floating mb-3">
                <input
                  :type="showRequestPassword2 ? 'text' : 'password'"
                  class="form-control"
                  :class="{ 'is-invalid': vRequest$.password2.$error }"
                  id="req_password2"
                  v-model="requestData.password2"
                  placeholder="Confirmar contraseña"
                  required
                >
                <label for="req_password2">
                  <i class="fas fa-lock text-muted me-2"></i>Confirmar contraseña
                </label>
                <span class="password-toggle" @click="showRequestPassword2 = !showRequestPassword2">
                  <i :class="showRequestPassword2 ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </span>
                <div class="invalid-feedback" v-if="vRequest$.password2.$error">
                  {{ vRequest$.password2.$errors[0].$message }}
                </div>
              </div>

              <!-- Teléfono y DNI -->
              <div class="row mb-3">
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                  <div class="form-floating">
                    <input
                      type="tel"
                      class="form-control"
                      :class="{ 'is-invalid': vRequest$.phone_number.$error }"
                      id="req_phone"
                      v-model="requestData.phone_number"
                      placeholder="Teléfono"
                      inputmode="numeric"
                      required
                    >
                    <label for="req_phone">
                      <i class="fas fa-phone text-muted me-2"></i>Teléfono
                    </label>
                    <div class="invalid-feedback" v-if="vRequest$.phone_number.$error">
                      {{ vRequest$.phone_number.$errors[0].$message }}
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-floating">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': vRequest$.dni.$error }"
                      id="req_dni"
                      v-model="requestData.dni"
                      placeholder="DNI"
                      inputmode="numeric"
                      maxlength="20"
                      required
                    >
                    <label for="req_dni">
                      <i class="fas fa-id-card text-muted me-2"></i>DNI
                    </label>
                    <div class="invalid-feedback" v-if="vRequest$.dni.$error">
                      {{ vRequest$.dni.$errors[0].$message }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tipo de negocio / perfil -->
              <div class="mb-3">
                <label for="req_role" class="form-label text-light small mb-2">
                  <i class="fas fa-briefcase text-muted me-2"></i>¿Qué tipo de negocio tenés?
                </label>
                <select
                  id="req_role"
                  class="form-select"
                  :class="{ 'is-invalid': vRequest$.requested_role.$error }"
                  v-model="requestData.requested_role"
                >
                  <option value="" disabled>Seleccioná tu perfil</option>
                  <option v-for="r in requestRoles" :key="r.slug" :value="r.slug">
                    {{ r.label }}{{ r.description ? ' — ' + r.description : '' }}
                  </option>
                </select>
                <div class="invalid-feedback d-block" v-if="vRequest$.requested_role.$error">
                  {{ vRequest$.requested_role.$errors[0].$message }}
                </div>
              </div>

              <!-- Nombre del negocio (si el perfil lo exige) -->
              <div v-if="requestRequiresBusiness" class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': vRequest$.business_name.$error }"
                  id="req_business_name"
                  v-model="requestData.business_name"
                  placeholder="Nombre del negocio"
                  required
                >
                <label for="req_business_name">
                  <i class="fas fa-store text-muted me-2"></i>Nombre del negocio
                </label>
                <div class="invalid-feedback" v-if="vRequest$.business_name.$error">
                  {{ vRequest$.business_name.$errors[0].$message }}
                </div>
              </div>

              <!-- Instagram (opcional) -->
              <div class="form-floating mb-3">
                <input
                  type="text"
                  class="form-control"
                  id="req_instagram"
                  v-model="requestData.instagram"
                  placeholder="Instagram (opcional)"
                >
                <label for="req_instagram">
                  <i class="fab fa-instagram text-muted me-2"></i>Instagram (opcional)
                </label>
              </div>

              <!-- Comentarios (opcional) -->
              <div class="mb-3">
                <label for="req_extra_info" class="form-label text-light small mb-2">
                  <i class="fas fa-comment text-muted me-2"></i>Comentarios (opcional)
                </label>
                <textarea
                  id="req_extra_info"
                  class="form-control"
                  v-model="requestData.extra_info"
                  rows="3"
                  placeholder="Contanos algo más sobre tu negocio…"
                ></textarea>
              </div>

              <!-- Error -->
              <div v-if="requestError" class="alert alert-danger mb-3">
                {{ requestError }}
              </div>

              <!-- Botón continuar -->
              <button
                type="button"
                class="btn btn-primary w-100 mb-3 py-3"
                @click="goToEmailConfirm"
              >
                Continuar
              </button>

              <!-- Link a Login -->
              <div class="text-center">
                <p class="text-muted">
                  ¿Ya tienes una cuenta?
                  <router-link to="/login" class="text-primary text-decoration-none">
                    Iniciar Sesión
                  </router-link>
                </p>
              </div>
              </template>

              <!-- Paso 2: confirmar email -->
              <div v-else-if="requestStep === 'confirmEmail'" class="text-center py-3">
                <div class="activation-icon mb-3">
                  <i class="fas fa-envelope"></i>
                </div>
                <p class="text-white mb-2">¿Enviamos el código de verificación a</p>
                <p class="mb-3"><strong class="text-primary">{{ requestData.email }}</strong>?</p>
                <p class="text-muted small mb-4">Te mandamos un código de 6 dígitos para confirmar tu email antes de enviar la solicitud.</p>
                <button
                  type="button"
                  class="btn btn-primary w-100 py-2 mb-2"
                  :disabled="sendingCode"
                  @click="sendEmailCode"
                >
                  <span v-if="sendingCode" class="spinner-border spinner-border-sm me-2"></span>
                  {{ sendingCode ? 'Enviando…' : 'Sí, enviar código' }}
                </button>
                <button type="button" class="btn btn-link text-primary" @click="backToRequestForm">
                  Cambiar email
                </button>
              </div>

              <!-- Paso 3: ingresar código -->
              <div v-else class="py-2">
                <div class="text-center mb-3">
                  <div class="activation-icon mb-3">
                    <i class="fas fa-shield-alt"></i>
                  </div>
                  <p class="text-white mb-1">Te enviamos un código a</p>
                  <p class="mb-2"><strong class="text-primary">{{ requestData.email }}</strong></p>
                </div>

                <div class="form-floating mb-3">
                  <input
                    type="text"
                    class="form-control verification-code-input"
                    :class="{ 'is-invalid': codeError }"
                    id="req_email_code"
                    v-model="verificationCode"
                    placeholder="Código de 6 dígitos"
                    inputmode="numeric"
                    maxlength="6"
                    autocomplete="one-time-code"
                  >
                  <label for="req_email_code">
                    <i class="fas fa-key text-muted me-2"></i>Código de verificación
                  </label>
                  <div v-if="codeError" class="invalid-feedback d-block">{{ codeError }}</div>
                </div>

                <div v-if="requestError" class="alert alert-danger mb-3">{{ requestError }}</div>

                <button
                  type="button"
                  class="btn btn-primary w-100 mb-2 py-3"
                  :disabled="submittingRequest"
                  @click="submitRequest"
                >
                  <span v-if="submittingRequest" class="spinner-border spinner-border-sm me-2"></span>
                  {{ submittingRequest ? 'Enviando…' : 'Confirmar y enviar solicitud' }}
                </button>

                <div class="d-flex justify-content-between align-items-center">
                  <button type="button" class="btn btn-link text-muted small" @click="backToRequestForm">
                    Cambiar email
                  </button>
                  <button type="button" class="btn btn-link text-primary small" :disabled="sendingCode" @click="sendEmailCode">
                    {{ sendingCode ? 'Enviando…' : 'Reenviar código' }}
                  </button>
                </div>
              </div>
            </form>
          </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, unref, nextTick } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, email, helpers } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { register, resendVerification } from '@/services/auth'
import { validateInvitationCode } from '@/services/invitations'
import { getPublicRoles, submitAccessRequest, sendAccessEmailCode } from '@/services/accessRequests'
import { getPublicSiteConfig } from '@/services/siteConfig'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

const FIELD_LABELS = {
  first_name: 'Nombre',
  last_name: 'Apellido',
  username: 'Usuario',
  phone_number: 'Teléfono',
  dni: 'DNI',
  email: 'Email',
  password: 'Contraseña',
  password2: 'Confirmar contraseña',
  invitation_code: 'Código de invitación',
  business_name: 'Nombre del negocio',
  business_address: 'Dirección del negocio',
  business_type: 'Tipo de negocio',
  instagram: 'Instagram',
}

const FORM_FIELD_KEYS = Object.keys(FIELD_LABELS)

const toastError = (message, fallback = 'Revisá los datos e intentá de nuevo.') => {
  const text = unref(message)
  const msg = text != null && String(text).trim() ? String(text).trim() : fallback
  toast.error(msg)
}

const formatFieldError = (field, message) => {
  const msg = String(unref(message) ?? '').trim()
  if (!msg) return ''
  const label = FIELD_LABELS[field] || field
  if (msg.toLowerCase().startsWith(label.toLowerCase()) || msg.toLowerCase().includes(field)) {
    return msg
  }
  return `${label}: ${msg}`
}

const collectValidationMessages = (v) => {
  const messages = []
  const add = (raw) => {
    const msg = String(unref(raw) ?? '').trim()
    if (msg && !messages.includes(msg)) messages.push(msg)
  }
  // Flat $errors list (populated after $validate + $touch)
  for (const err of v.$errors || []) add(err?.$message)
  // Field-by-field fallback (handles cases where $errors is not flat)
  for (const key of FORM_FIELD_KEYS) {
    const field = v[key]
    if (!field) continue
    if (field.$errors?.length) {
      for (const err of field.$errors) add(err.$message)
    } else if (field.$dirty && field.$invalid) {
      // Last-resort: field is invalid but message not extractable
      const label = FIELD_LABELS[key] || key
      add(`${label}: revisá este campo`)
    }
  }
  return messages
}

const extractRegisterApiErrors = (error) => {
  const normalize = (field, val) => formatFieldError(field, Array.isArray(val) ? val[0] : val)

  if (error?.errors?.length) {
    return error.errors
      .map((entry) => {
        const text = String(entry).trim()
        if (!text) return ''
        const colon = text.indexOf(':')
        if (colon > 0) {
          const field = text.slice(0, colon).trim()
          const msg = text.slice(colon + 1).trim()
          return formatFieldError(field, msg)
        }
        return text
      })
      .filter(Boolean)
  }

  const data = error?.response?.data
  if (!data) return []

  if (Array.isArray(data.error)) {
    return data.error
      .map((entry) => {
        const text = String(entry).trim()
        if (!text) return ''
        const colon = text.indexOf(':')
        if (colon > 0) {
          return formatFieldError(text.slice(0, colon).trim(), text.slice(colon + 1).trim())
        }
        return text
      })
      .filter(Boolean)
  }
  if (typeof data.error === 'string' && data.error.trim()) {
    return [data.message?.trim() || data.error.trim()]
  }
  if (data.error && typeof data.error === 'object') {
    return Object.entries(data.error)
      .map(([field, val]) => normalize(field, val))
      .filter(Boolean)
  }
  if (data.message?.trim()) {
    return [data.message.trim()]
  }
  return []
}

// Estado del formulario
const formData = reactive({
  first_name: '',
  last_name: '',
  username: '',
  phone_number: '',
  dni: '',
  instagram: '',
  email: '',
  password: '',
  password2: '',
  invitation_code: '',
  business_name: '',
  business_address: '',
  business_type: '',
})

const inviteError = ref('')
const inviteRoleLabel = ref('')
const requiresBusinessInfo = ref(false)
const inviteValidating = ref(false)

const validateInviteCode = async () => {
  const code = (formData.invitation_code || '').trim()
  if (!code) {
    inviteError.value = 'El código de invitación es requerido'
    inviteRoleLabel.value = ''
    requiresBusinessInfo.value = false
    return false
  }
  inviteValidating.value = true
  try {
    const res = await validateInvitationCode(code)
    if (!res.valid) {
      inviteRoleLabel.value = ''
      requiresBusinessInfo.value = false
      inviteError.value = res.message || 'Código inválido'
      return false
    }
    inviteError.value = ''
    inviteRoleLabel.value = res.role?.label || ''
    requiresBusinessInfo.value = !!res.role?.requires_business_info
    return true
  } catch {
    inviteRoleLabel.value = ''
    requiresBusinessInfo.value = false
    inviteError.value = 'No se pudo validar el código'
    return false
  } finally {
    inviteValidating.value = false
  }
}

// Estado UI
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const showAccountExistsMessage = ref(false)
const accountExistsMessage = ref('')

const showActivationPending = ref(false)
const registerPending = ref(false)
const isResending = ref(false)
const registeredEmail = ref('')

watch(() => formData.invitation_code, (code) => {
  if (!(code || '').trim()) {
    requiresBusinessInfo.value = false
    inviteRoleLabel.value = ''
    inviteError.value = ''
    formData.business_name = ''
    formData.business_address = ''
    formData.business_type = ''
  }
})

onMounted(() => {
  document.title = 'Crear Cuenta - Kame Seeds'
  loadAccessRequestSetting()
})

const phonePattern = helpers.withMessage(
  'Ingresá un teléfono válido (solo números, mínimo 10 dígitos)',
  helpers.regex(/^[0-9]{10,15}$/)
)

const dniPattern = helpers.withMessage(
  'Ingresá un DNI válido (solo números, 7 a 8 dígitos)',
  helpers.regex(/^[0-9]{7,8}$/)
)

const usernameAlphanumeric = helpers.withMessage(
  'El usuario solo puede contener letras y números',
  (value) => /^[a-zA-Z0-9]+$/.test(String(value || ''))
)

const requiredTrimmed = (message) =>
  helpers.withMessage(message, (value) => String(value ?? '').trim().length > 0)

const minLengthTrimmed = (min, message) =>
  helpers.withMessage(message, (value) => String(value ?? '').trim().length >= min)

const rules = computed(() => {
  const base = {
    first_name: {
      required: helpers.withMessage('El nombre es requerido', required),
      minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2)),
    },
    last_name: {
      required: helpers.withMessage('El apellido es requerido', required),
      minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2)),
    },
    username: {
      required: helpers.withMessage('El nombre de usuario es requerido', required),
      minLength: helpers.withMessage(
        'El nombre de usuario debe tener al menos 4 caracteres',
        minLength(4)
      ),
      usernameAlphanumeric,
    },
    phone_number: {
      required: helpers.withMessage('El teléfono es requerido', required),
      phonePattern,
    },
    dni: {
      required: helpers.withMessage('El DNI es requerido', required),
      dniPattern,
    },
    email: {
      required: helpers.withMessage('El email es requerido', required),
      email: helpers.withMessage('Ingrese un email válido', email),
    },
    password: {
      required: helpers.withMessage('La contraseña es requerida', required),
      minLength: helpers.withMessage(
        'La contraseña debe tener al menos 8 caracteres',
        minLength(8)
      ),
    },
    password2: {
      required: helpers.withMessage('Debe confirmar la contraseña', required),
      sameAsPassword: helpers.withMessage(
        'Las contraseñas no coinciden',
        (value) => value === formData.password
      ),
    },
    invitation_code: {
      required: requiredTrimmed('El código de invitación es requerido'),
    },
  }

  if (requiresBusinessInfo.value) {
    return {
      ...base,
      business_name: {
        required: requiredTrimmed('Ingresá el nombre del negocio'),
        minLength: minLengthTrimmed(2, 'El nombre del negocio debe tener al menos 2 caracteres'),
      },
      business_address: {
        required: requiredTrimmed('Ingresá la dirección del negocio'),
        minLength: minLengthTrimmed(5, 'La dirección del negocio debe tener al menos 5 caracteres'),
      },
      business_type: {
        required: requiredTrimmed('Ingresá el tipo de negocio (ej: growshop, dispensario)'),
        minLength: minLengthTrimmed(2, 'El tipo de negocio debe tener al menos 2 caracteres'),
      },
    }
  }

  return base
})

const v$ = useVuelidate(rules, formData)

// ── Modo "Solicitar acceso" (sin código de invitación) ─────────────
const mode = ref('code')
const accessRequestsEnabled = ref(true)
const showRequestSuccess = ref(false)
const submittingRequest = ref(false)
const requestError = ref('')
const requestRoles = ref([])
const requestStep = ref('form')
const verificationCode = ref('')
const sendingCode = ref(false)
const codeError = ref('')
const showRequestPassword = ref(false)
const showRequestPassword2 = ref(false)

const requestData = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password2: '',
  phone_number: '',
  dni: '',
  requested_role: '',
  business_name: '',
  instagram: '',
  extra_info: '',
})

const selectedRequestRole = computed(
  () => requestRoles.value.find((r) => r.slug === requestData.requested_role) || null
)
const requestRequiresBusiness = computed(() => !!selectedRequestRole.value?.requires_business_info)

const pageTitle = computed(() => {
  if (showActivationPending.value) return 'Revisá tu email'
  if (showRequestSuccess.value) return 'Solicitud enviada'
  if (mode.value === 'request') return 'Solicitar acceso'
  return 'Crear Cuenta'
})

const setMode = (nextMode) => {
  if (nextMode === 'request' && !accessRequestsEnabled.value) return
  mode.value = nextMode
  requestError.value = ''
  requestStep.value = 'form'
  verificationCode.value = ''
  codeError.value = ''
  if (nextMode === 'request') {
    loadRequestRoles()
  }
}

const loadAccessRequestSetting = async () => {
  try {
    const config = await getPublicSiteConfig()
    accessRequestsEnabled.value = config.access_requests_enabled !== false
    if (!accessRequestsEnabled.value && mode.value === 'request') mode.value = 'code'
  } catch {
    accessRequestsEnabled.value = true
  }
}

const loadRequestRoles = async () => {
  if (requestRoles.value.length) return
  try {
    requestRoles.value = await getPublicRoles()
  } catch {
    requestRoles.value = []
  }
}

const requestRules = computed(() => {
  const base = {
    first_name: { required: helpers.withMessage('El nombre es requerido', required) },
    last_name: { required: helpers.withMessage('El apellido es requerido', required) },
    email: {
      required: helpers.withMessage('El email es requerido', required),
      email: helpers.withMessage('Ingresá un email válido', email),
    },
    phone_number: {
      required: helpers.withMessage('El teléfono es requerido', required),
      phonePattern,
    },
    dni: {
      required: helpers.withMessage('El DNI es requerido', required),
      dniPattern,
    },
    password: {
      required: helpers.withMessage('La contraseña es requerida', required),
      minLength: helpers.withMessage(
        'La contraseña debe tener al menos 8 caracteres',
        minLength(8)
      ),
    },
    password2: {
      required: helpers.withMessage('Confirmá la contraseña', required),
      sameAsPassword: helpers.withMessage(
        'Las contraseñas no coinciden',
        (value) => value === requestData.password
      ),
    },
    requested_role: { required: requiredTrimmed('Seleccioná tu perfil') },
  }
  if (requestRequiresBusiness.value) {
    base.business_name = { required: requiredTrimmed('Ingresá el nombre del negocio') }
  }
  return base
})

const vRequest$ = useVuelidate(requestRules, requestData)

const extractRequestApiError = (error) => {
  if (Array.isArray(error)) {
    return error
      .map((entry) => {
        const text = String(entry).trim()
        const colon = text.indexOf(':')
        return colon > 0 ? text.slice(colon + 1).trim() : text
      })
      .filter(Boolean)
      .join(' ')
  }
  if (error && typeof error === 'object') {
    return Object.entries(error)
      .map(([field, value]) => {
        const raw = Array.isArray(value) ? value[0] : value
        return `${field}: ${raw}`
      })
      .join(' ')
  }
  return String(error || '')
}

const goToEmailConfirm = async () => {
  requestError.value = ''
  const valid = await vRequest$.value.$validate()
  if (!valid) {
    vRequest$.value.$touch()
    return
  }
  requestStep.value = 'confirmEmail'
}

const sendEmailCode = async () => {
  requestError.value = ''
  codeError.value = ''
  sendingCode.value = true
  try {
    await sendAccessEmailCode(requestData.email)
    requestStep.value = 'verifyCode'
  } catch (error) {
    const data = error?.response?.data
    requestError.value = data?.error || 'No se pudo enviar el código. Intentá nuevamente.'
    toast.error(requestError.value)
  } finally {
    sendingCode.value = false
  }
}

const backToRequestForm = () => {
  requestStep.value = 'form'
  verificationCode.value = ''
  codeError.value = ''
}

const submitRequest = async () => {
  requestError.value = ''
  codeError.value = ''

  const code = (verificationCode.value || '').trim()
  if (!code) {
    codeError.value = 'Ingresá el código de verificación.'
    return
  }

  submittingRequest.value = true
  try {
    await submitAccessRequest({
      first_name: (requestData.first_name || '').trim(),
      last_name: (requestData.last_name || '').trim(),
      email: (requestData.email || '').trim(),
      phone_number: (requestData.phone_number || '').replace(/\D/g, ''),
      dni: (requestData.dni || '').replace(/\D/g, ''),
      requested_role: requestData.requested_role,
      business_name: (requestData.business_name || '').trim(),
      instagram: (requestData.instagram || '').trim(),
      extra_info: (requestData.extra_info || '').trim(),
      password: requestData.password,
      password2: requestData.password2,
      email_verification_code: code,
    })
    showRequestSuccess.value = true
  } catch (error) {
    const data = error?.response?.data
    const message = data?.error ? extractRequestApiError(data.error) : ''
    if (/código/i.test(message)) {
      codeError.value = message
    } else {
      requestError.value = message || 'No se pudo enviar la solicitud. Intentá nuevamente.'
      toast.error(requestError.value)
    }
  } finally {
    submittingRequest.value = false
  }
}

const showValidationErrors = (messages) => {
  if (!messages.length) {
    toastError(null)
    return
  }
  errorMessage.value = messages.join('\n')
  messages.forEach((msg) => toast.error(msg, { timeout: 6000 }))
  v$.value.$touch()
}

// Manejo del submit actualizado
const handleSubmit = async () => {
  try {
    errorMessage.value = ''
    showAccountExistsMessage.value = false

    const code = (formData.invitation_code || '').trim()

    if (!code) {
      inviteError.value = 'El código de invitación es requerido'
      toastError(inviteError.value)
      v$.value.invitation_code?.$touch()
      return
    }

    const inviteOk = await validateInviteCode()
    if (!inviteOk) {
      toastError(inviteError.value, 'Código de invitación inválido')
      return
    }
    // Esperar que Vue + Vuelidate actualicen las reglas con el nuevo requiresBusinessInfo
    await nextTick()
    await nextTick()

    const isFormValid = await v$.value.$validate()
    if (!isFormValid) {
      showValidationErrors(collectValidationMessages(v$.value))
      return
    }
    
    isLoading.value = true
    registerPending.value = true

    const payload = {
      ...formData,
      invitation_code: (formData.invitation_code || '').trim(),
      phone_number: formData.phone_number.replace(/\D/g, ''),
      dni: formData.dni.replace(/\D/g, ''),
      business_name: (formData.business_name || '').trim(),
      business_address: (formData.business_address || '').trim(),
      business_type: (formData.business_type || '').trim(),
    }

    let registerError = null
    let registerResponse = null

    const registerTask = register(payload)
      .then((response) => {
        registerResponse = response
      })
      .catch((error) => {
        registerError = error
      })

    // Mostrar pantalla de activación tras 2s aunque el email siga enviándose
    await new Promise((resolve) => setTimeout(resolve, 2000))
    isLoading.value = false
    showActivationPending.value = true
    registeredEmail.value = formData.email

    await registerTask
    registerPending.value = false

    if (registerError) {
      showActivationPending.value = false
      const error = registerError

      errorMessage.value = ''
      showAccountExistsMessage.value = false

      if (error.response?.data?.error === 'ACCOUNT_EXISTS_UNVERIFIED') {
        showAccountExistsMessage.value = true
        accountExistsMessage.value = error.response.data.message
        return
      }

      if (error.response?.data?.error === 'ACCOUNT_NOT_VERIFIED') {
        showActivationPending.value = true
        registeredEmail.value = error.response.data.email || formData.email
        toast.warning(error.response.data.message)
        return
      }

      if (error.response?.data?.error === 'ACCOUNT_EXISTS') {
        toastError(error.response.data.message, 'Ya existe una cuenta con esos datos')
        setTimeout(() => {
          router.push('/login')
        }, 2000)
        return
      }

      const apiMessages = extractRegisterApiErrors(error)
      if (apiMessages.length) {
        errorMessage.value = apiMessages.join('\n')
        apiMessages.forEach((msg) => toast.error(msg, { timeout: 6000 }))
        for (const field of FORM_FIELD_KEYS) {
          const label = FIELD_LABELS[field]
          if (
            apiMessages.some(
              (m) =>
                m.toLowerCase().includes(label.toLowerCase()) ||
                m.toLowerCase().includes(field)
            ) &&
            v$.value[field]
          ) {
            v$.value[field].$touch()
          }
        }
        return
      }

      toastError(
        error.response?.data?.message || error.message,
        'Error al crear la cuenta. Por favor, intentá nuevamente.'
      )
      errorMessage.value = error.response?.data?.message || error.message || ''
      return
    }

    if (registerResponse?.email) {
      registeredEmail.value = registerResponse.email
    }
  } catch (error) {
    console.error('Error en registro:', error)
    isLoading.value = false
    registerPending.value = false
    showActivationPending.value = false
    toastError(error.message, 'Error al crear la cuenta. Por favor, intentá nuevamente.')
  } finally {
    if (!showActivationPending.value) {
      isLoading.value = false
    }
    registerPending.value = false
  }
}

const resendActivationLink = async () => {
  try {
    isResending.value = true
    await resendVerification(registeredEmail.value || formData.email)
    toast.success('Email de activación reenviado')
  } catch (error) {
    if (error.retryAfterSeconds) {
      toast.warning(error.message)
    } else {
      toast.error(error.response?.data?.error || error.message || 'Error al reenviar el email')
    }
  } finally {
    isResending.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  width: 100%;
  background-color: var(--black-color);
  background-image: url('@/assets/images/fondos/fondo-catalogo.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  font-family: 'Poppins', sans-serif;
}

.auth-card {
  transition: all 0.3s ease;
  background-color: rgba(27, 27, 27, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.mode-toggle {
  display: flex;
  gap: 0.5rem;
  padding: 0.35rem;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
}

.mode-toggle__btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-toggle__btn:hover {
  color: #ffffff;
}

.mode-toggle__btn.active {
  background: var(--primary-color, #ffec00);
  color: #111111;
}

.mode-toggle__btn.active i {
  color: #111111;
}

.form-select {
  background-color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: var(--black-color);
  font-family: 'Poppins', sans-serif;
  width: 100%;
  max-width: 100%;
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem rgba(255, 236, 0, 0.2);
}

.form-label {
  font-family: 'Poppins', sans-serif;
}

textarea.form-control {
  background-color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: var(--black-color);
}

textarea.form-control:focus {
  background-color: rgba(255, 255, 255, 0.95);
  border-color: var(--primary-color);
  color: var(--black-color);
  box-shadow: 0 0 0 0.25rem rgba(255, 236, 0, 0.2);
}

.auth-card .form-floating > .form-control {
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: var(--black-color);
}

.auth-card .form-floating > .form-control:focus {
  background-color: rgba(255, 255, 255, 0.95);
  border-color: var(--primary-color);
  color: var(--black-color);
  box-shadow: 0 0 0 0.25rem rgba(255, 236, 0, 0.2);
}

.auth-card .form-floating > label {
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.6);
}

.auth-card .form-floating > label .fas,
.auth-card .form-floating > label .fab {
  font-family: 'Font Awesome 6 Free' !important;
  font-style: normal;
}

.auth-card .form-floating > label .fab {
  font-family: 'Font Awesome 6 Brands' !important;
}

.auth-card .form-floating > .form-control:focus ~ label,
.auth-card .form-floating > .form-control:not(:placeholder-shown) ~ label {
  color: rgba(0, 0, 0, 0.75);
}

.auth-card .form-floating > label::after {
  background-color: rgba(255, 255, 255, 0.8);
}

.auth-card .form-floating > .form-control:focus ~ label::after,
.auth-card .form-floating > .form-control:not(:placeholder-shown) ~ label::after {
  background-color: rgba(255, 255, 255, 0.95);
}

.auth-card .form-text {
  color: rgba(255, 255, 255, 0.65) !important;
}

.auth-card .form-floating label .text-muted {
  color: rgba(0, 0, 0, 0.45) !important;
}

.form-floating input {
  padding-right: 2.5rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  z-index: 10;
}

.password-toggle:hover {
  color: rgba(0, 0, 0, 0.7);
}

.btn-primary {
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

@media (max-width: 576px) {
  .auth-card {
    margin: 0 0.5rem;
  }
}

.verification-code-input input {
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  text-align: center;
}

.btn-link {
  color: var(--primary-color);
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

/* Tooltip de Instagram */
.instagram-tooltip {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  cursor: help;
}

.instagram-tooltip .fas.fa-info-circle {
  font-size: 1rem;
  color: #6c757d;
  transition: color 0.2s ease;
}

.instagram-tooltip:hover .fas.fa-info-circle {
  color: #495057;
}

.tooltip-content {
  position: absolute;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: #343a40;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
}

.tooltip-content::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-left-color: #343a40;
}

.instagram-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

/* Ajustar padding del input de Instagram para hacer espacio al icono */
#instagram {
  padding-right: 3rem !important;
}

.activation-pending {
  padding: 0.5rem 0;
}

.activation-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(255, 236, 0, 0.12);
  color: var(--primary-color, #ffec00);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

@media (max-width: 768px) {
  .instagram-tooltip {
    right: -10px;
  }
  
  .tooltip-content {
    right: 25px;
    width: 250px;
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .tooltip-content {
    right: 25px;
    width: 200px;
    font-size: 0.75rem;
    padding: 10px 12px;
  }
}
</style>
